/**
 * Activity — "where is everyone in Atlas right now, which pages and customers
 * get used, and who is not using the app at all?"
 *
 * The pure shared contract between the client beacon and the server. No
 * imports, so both sides hold the same numbers and the timing invariant below
 * is checkable in one unit test rather than drifting across two files.
 *
 * ── TWO STORES, ON PURPOSE ────────────────────────────────────────────────
 *
 * LIVE (Redis, `activity:live`) — who is on what right now. Expires in
 * STALE_MS; a sorted set is exactly the right shape and nothing is lost when
 * Redis restarts.
 *
 * HISTORY (Postgres, `worldvue.activity_page_usage`) — every hour of usage,
 * kept indefinitely. This started in Redis too, and moving it is the point:
 * Redis caps history at whatever the shared 256MB `allkeys-lru` cache holds,
 * about 48 hours before these keys start evicting insights/liveview/
 * notifications keys. Adoption is the question actually being asked — "who
 * should be using this and isn't" — and 48 hours cannot answer it, because a
 * Monday check would flag everyone who took the weekend off.
 *
 * ── WHY THIS IS NOT PRESENCE ──────────────────────────────────────────────
 *
 * Presence (`src/lib/presence/*`) answers "is a colleague already in THIS
 * record?" for one room the viewer is standing in. It cannot answer "where is
 * everyone", for two reasons that are structural rather than missing features:
 *
 *   1. COVERAGE. A presence room only exists where `deriveScopeChain` finds a
 *      scope-bearing param, and only for the `internal` app. Every list page,
 *      dashboard and report — and every other app — has no room at all.
 *   2. ENUMERABILITY. Presence keys are named after records with no index, so
 *      listing them means `SCAN presence:*` — the exact operation
 *      `src/server/presence/store.ts` exists to forbid, after the 2025
 *      implementation's 60-second `KEYS` sweep stalled a Redis cluster shared
 *      with insights, liveview and notifications for five months.
 */

/**
 * How often a visible tab reports where it is.
 *
 * Three times presence's HEARTBEAT_MS, deliberately. Presence beats only on
 * scoped record pages; this beats on EVERY route for every internal user, so
 * the traffic base is far broader. One request per minute per user keeps the
 * write side negligible, and it makes one beat worth exactly one minute of
 * dwell, which is what the reported "viewer minutes" means.
 */
export const BEAT_MS = 60_000

/**
 * How long a viewer survives without a beat before readers treat them as gone.
 *
 * Must stay at least 2× BEAT_MS so a single dropped request never makes someone
 * blink off the board — `config.test.ts` asserts this.
 */
export const STALE_MS = 150_000

/**
 * Whole-key TTL for the live set, refreshed on every beat. This is what makes
 * the Redis side self-limiting: once nobody is active the key disappears on its
 * own, so nothing ever has to scan for keys to clean up. Comfortably longer
 * than STALE_MS so the key outlives its last trim.
 */
export const LIVE_TTL_S = 300

/**
 * Most live rows ever read. A ceiling on one `ZREVRANGE`, not a real limit —
 * the set is bounded by concurrent internal staff, far below this. `liveTotal`
 * comes from `ZCARD`, so a saturated read still reports honestly.
 */
export const MAX_LIVE = 500

/** Consecutive beat failures before the client stops beating. */
export const MAX_BEAT_FAILURES = 3

/**
 * How long a route must stay put before its first beat.
 *
 * The client beats on arrival as well as on the interval, so without this a
 * user clicking through five pages in three seconds would file five arrivals —
 * inflating visit counts for pages nobody read, and firing four requests that
 * get aborted a moment later. A page you leave inside a second and a half was
 * not a visit.
 */
export const SETTLE_MS = 1_500

/**
 * PostHog flag gating the WRITE side. Off stops all beacon traffic at source
 * with no deploy, exactly like `presence-viewers`. History already collected
 * stays queryable; only new beats stop.
 */
export const ACTIVITY_FLAG = 'activity-beacon'

/**
 * Reporting windows. `all` means no lower bound — the reason history moved to
 * Postgres, and the answer to "which customers are most visited, ever".
 */
export const ACTIVITY_WINDOWS = ['24h', '7d', '30d', 'all'] as const

export type ActivityWindow = (typeof ACTIVITY_WINDOWS)[number]

export const DEFAULT_WINDOW: ActivityWindow = '7d'

/** Hours back for each window; null for all-time (no lower bound). */
export const WINDOW_HOURS: Record<ActivityWindow, number | null> = {
  '24h': 24,
  '7d': 24 * 7,
  '30d': 24 * 30,
  all: null
}

export const WINDOW_LABELS: Record<ActivityWindow, string> = {
  '24h': 'Last 24 hours',
  '7d': 'Last 7 days',
  '30d': 'Last 30 days',
  all: 'All time'
}

/**
 * Clamp a requested window to one we actually offer.
 *
 * The window arrives as a query param and is therefore untrusted. Snapping to
 * the allowed list rather than parsing a duration keeps the SQL bound to one of
 * four known shapes.
 */
export function normalizeWindow(value: unknown): ActivityWindow {
  return ACTIVITY_WINDOWS.find((w) => w === value) ?? DEFAULT_WINDOW
}

/** Most rows any usage table returns. */
export const MAX_ROWS = 200

/**
 * How far back a last-login lookup reaches.
 *
 * `audit.audit_log` is monthly-partitioned and EVERY query must constrain
 * `created_at` or the planner scans every partition (see
 * `src/app/api/admin/audit-logs/filters.ts`). "Last login ever" is inherently
 * unbounded, so it is bounded here instead — which means a null result reads as
 * "no login inside this window", NOT "never". Lives in the shared contract so
 * the column label and the query can never disagree about the number.
 */
export const LOGIN_LOOKBACK_DAYS = 180

/**
 * Most individual records shown inside one expanded page row.
 *
 * A busy customer can have hundreds of cases, and a foldout that long is not a
 * detail view. Records past this are summed into a single remainder row so the
 * group's total still matches the page's real total — truncating without it
 * would make the parent disagree with its children.
 */
export const MAX_RECORDS_PER_PAGE = 25

/** One person on one page, as the live census returns them. */
export type ActivityViewer = {
  userId: string
  name: string
  /** Ready-to-use URL from `getAvatarUrl`, never the raw S3 key. */
  image: string | null
  /** App key from the `applications` registry, e.g. `internal`, `sales`. */
  app: string
  /** Human page label from the matched route config, e.g. `VLANs`. */
  page: string
  /** The pathname they are on — the drill-in link for the admin board. */
  pathname: string
  /** Customer/property the page sits under; null on an org-level page. */
  customerId: string | null
  /** Resolved customer name, when we could find one. */
  customerName: string | null
  /** Epoch ms of their last beat. */
  lastSeenAt: number
  organizationId: string
}

/**
 * One page's usage over the window — a LEAF row.
 *
 * Rows are per route AND customer, so `/internal/S5000/overview` and
 * `/internal/Y-0020/overview` rank separately even though they share a route.
 *
 * Deliberately flat rather than nested. The table's `grouping` config builds the
 * expandable parents from `groupKey`, and it passes a single-child group through
 * unwrapped — so a page with several records folds out and a page with none
 * renders as a plain row, with no branching here or in the component.
 */
export type ActivityPageStat = {
  /** Stable row id — `<routeKey>|<customerId>|<recordId>`. */
  key: string
  /** What the foldout groups on — `<routeKey>|<customerId>`. */
  groupKey: string
  /** Matched route config key, e.g. `/internal/[id]/support/cases/[caseId]`. */
  routeKey: string
  app: string
  /** Human page label from the route config. */
  label: string
  customerId: string | null
  customerName: string | null
  /** The individual record this row counts, null when the page has none. */
  recordId: string | null
  /**
   * Navigable pathname, or null when the route needs ids we did not store — a
   * remainder row standing in for records past the display cap has no URL.
   */
  href: string | null
  /**
   * Beats recorded. One beat is BEAT_MS of a person sitting on the page, so
   * this is dwell — viewer-minutes at a 60s beat — not a visit count. The two
   * are reported separately because reporting only visits would rank a page
   * nobody stays on above one people work in all day.
   */
  viewerMinutes: number
  /** Arrivals: beats where the viewer's route changed. Closer to "visits". */
  visits: number
}

/** One customer's usage over the window, across every page. */
export type ActivityCustomerStat = {
  customerId: string
  customerName: string | null
  href: string | null
  /** Distinct pages touched under this customer. */
  pages: number
  /** Distinct staff who touched it. */
  viewers: number
  viewerMinutes: number
  visits: number
}

/**
 * One user's usage over the window.
 *
 * Produced by LEFT JOINing the internal-staff roster onto the usage table, so
 * somebody who never opened Atlas appears with zeroes rather than being absent.
 * That asymmetry is the whole point of the tab: an empty row is the finding.
 */
export type ActivityUserStat = {
  userId: string
  name: string
  email: string | null
  image: string | null
  viewerMinutes: number
  visits: number
  /** Distinct pages they touched. */
  pages: number
  /** Epoch ms of their most recent beat, null if they never beat. */
  lastActiveAt: number | null
  /**
   * Epoch ms of their last sign-in, from the audit log's login/sso_login
   * events. Null means no login inside the audit log's retention — which is
   * not the same as "never", and the UI says so.
   */
  lastLoginAt: number | null
  /** They hold a session that has not expired. */
  signedIn: boolean
}

/** Everything the admin page renders. */
export type ActivityCensus = {
  live: ActivityViewer[]
  /** Distinct people live — not `live.length` if MAX_LIVE bit. */
  liveTotal: number
  pages: ActivityPageStat[]
  customers: ActivityCustomerStat[]
  users: ActivityUserStat[]
  window: ActivityWindow
  generatedAt: number
  /**
   * True when the LIVE half could not be read. History lives in Postgres now,
   * so a Redis outage costs the live board only — the rest of the page is
   * unaffected, which is why this is not a whole-page error.
   */
  liveDegraded: boolean
}

export const EMPTY_CENSUS: ActivityCensus = {
  live: [],
  liveTotal: 0,
  pages: [],
  customers: [],
  users: [],
  window: DEFAULT_WINDOW,
  generatedAt: 0,
  liveDegraded: false
}
